import { CollisionPriority } from "@dnd-kit/abstract";
import { useSortable } from "@dnd-kit/react/sortable";
import React, { CSSProperties, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cloneObj } from "../../../../../../util/varibles/global";
import { TTaskTreatment } from "../../../../../../util/varibles/interface";
import { callEventEditOp, IUnitWhenEdit, setEditOp } from "../../EditOperationContext";
import { checkSorting } from "../constants";
import stylesContainer from "../style.module.scss";
import Task from "../Task";
import styles from './style.module.scss';
import Title from "./Title";

interface IProps {
    unit: IUnitWhenEdit
    index: number
    isDraggingUnit: boolean
}

const Unit: React.FC<IProps> = ({ unit, index, isDraggingUnit }) => {
    const { id, tasks = [], isLock, } = unit;
    const set = setEditOp();
    const { turnOnCalculate } = callEventEditOp();
    const turnOnCalculateRef = useRef(turnOnCalculate);
    const rootRef = useRef<HTMLDivElement | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);
    const [expandedHeight, setExpandedHeight] = useState<number>();
    const { isDragging, isDropTarget, ref, handleRef } = useSortable({
        id,
        index,
        type: 'unit',
        accept: ['unit', 'task', 'taskGroup'],
        group: 'units',
        collisionPriority: CollisionPriority.Low,
        disabled: isLock,
    });
    const setRootRef = useCallback((element: HTMLDivElement | null) => {
        rootRef.current = element;
        ref(element);
    }, [ref]);

    useLayoutEffect(() => {
        const content = contentRef.current;
        if (!content)
            return;

        const updateExpandedHeight = () => {
            if (rootRef.current)
                setExpandedHeight(rootRef.current.scrollHeight + 5);
        };
        const observer = new ResizeObserver(updateExpandedHeight);
        observer.observe(content);
        updateExpandedHeight();

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        turnOnCalculateRef.current = turnOnCalculate;
    }, [turnOnCalculate]);

    const handleUpdateTask = useCallback((value: TTaskTreatment, index: number, args?: { sorting?: boolean }) => {
        set(({ units }) => {
            const newTasks = cloneObj(units[id].tasks || []);
            newTasks?.splice(index, 1, value);
            const { sorting } = args || {};
            const newValue = { tasks: newTasks, isSorting: units[id].isSorting };
            if (sorting)
                newValue.isSorting = checkSorting(newTasks);
            return ({ units: { ...units, [id]: { ...units[id], ...newValue } } })
        })
        turnOnCalculateRef.current()
    }, [set, id])

    const handleDeleteTask = useCallback((index: number) => {
        set(({ units }) => {
            const newTasks = cloneObj(units[id].tasks || []);
            newTasks?.splice(index, 1);
            const isSorting = checkSorting(newTasks)
            return { units: { ...units, [id]: { ...units[id], isSorting, tasks: newTasks } } }
        })
        turnOnCalculateRef.current()
    }, [set, id])

    return <div
        ref={setRootRef}
        className={[styles.group, stylesContainer.group].join(' ')}
        style={{ '--unit-expanded-height': expandedHeight ? `${expandedHeight}px` : '9999px' } as CSSProperties}
        data-lock={isLock}
        data-dragging={isDragging || undefined}
        data-drag-over={isDropTarget || undefined}
        data-compact={isDraggingUnit || undefined}
    >
        <Title unit={unit} dragHandleRef={handleRef} isDragging={isDragging} />
        <div ref={contentRef} className={styles.content}>
            {tasks.map((item, subIndex: number) => <Task
                key={item.group_id}
                data={{ ...item, index: subIndex } as TTaskTreatment}
                unit={unit}
                onUpdate={(value, args) => handleUpdateTask(value, subIndex, args)}
                onDelete={() => handleDeleteTask(subIndex)}
            />)}
        </div>
    </div>
};

export default Unit;
